import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
// Re-add custom API/debug utils
import { getAuthApiBaseUrl, buildApiUrl } from '../utils/apiConfig';
import { logAuthEvent, logLocalStorageDetails, logTokenInfo } from '../utils/authDebugger';
// Keep Supabase client and profile helpers
import { supabase } from '../lib/supabase'; 
import { getProfile, updateProfile, createProfile } from '../lib/supabase'; 
import type { Database } from '../lib/database.types';
import type { Profile } from '../types/Profile'; // Import Profile from its new file
// Import userService
import { createUser, checkUserExists } from '../lib/userService';

// Add rate limiting constants
const SESSION_CHECK_MIN_INTERVAL = 60000; // 1 minute
let lastSessionCheckTime = 0;
let sessionCheckCount = 0;
let failedChecksCount = 0;
let isCheckingSession = false; // Add flag to prevent concurrent checks

/**
 * Safe logger for auth operations that hides sensitive details in production
 */
function logAuth(level: 'info' | 'warn' | 'error', message: string, details?: any) {
  // Always show error logs, but conditionally show details
  if (level === 'error') {
    console.error(`[AuthContext] ${message}`);
    if (!isProduction() && details) {
      console.error('Details:', details);
    }
    return;
  }
  
  // For non-error logs, completely hide in production
  if (isProduction()) return;
  
  if (level === 'info') {
    console.log(`[AuthContext] ${message}`, details);
  } else if (level === 'warn') {
    console.warn(`[AuthContext] ${message}`, details);
  }
}

// Interface matching the reverted custom flow + profile updates
interface AuthContextType {
  isAuthenticated: boolean;
  user: Profile | null; // Use Profile type
  login: (email: string) => Promise<{ devMagicLink?: string }>; // Reverted signature
  verifyToken: (token: string) => Promise<void>; // Reverted function
  logout: () => Promise<void>; // Reverted signature (async for fetch)
  isInitializing: boolean;
  resetAuthenticationState: () => void; // Reverted function
  updateUserPreferences: (updates: { 
    timezone?: string; 
    settings?: Database['public']['Tables']['profiles']['Update']['settings']; 
  }) => Promise<{ error?: Error }>; // Keep this
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Simple environment detection
const detectEnvironment = () => {
  if (typeof window === 'undefined') return 'staging';
  const hostname = window.location.hostname;
  if (hostname === 'app.sequenceplanner.com' || hostname === 'sequenceplanner.com') {
    return 'production';
  }
  return 'staging';
};

const isProduction = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'app.sequenceplanner.com' || window.location.hostname === 'sequenceplanner.com';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Profile | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // CRITICAL FIX: Create ref at component level instead of inside useEffect
  const hasInitRunRef = useRef(false);

  const fetchUserProfile = useCallback(async (userId: string) => {
    console.log('[AuthContext] ====== FETCH_USER_PROFILE_CALLED ======');
    console.log('[AuthContext] Fetching profile for userId:', userId);
    console.log('[AuthContext] Current state:', { 
      isAuthenticated, 
      hasUser: !!user, 
      userId: user?.id
    });

    try {
      // First try to get the existing profile
      console.log('[AuthContext] Attempting to get profile from database for userId:', userId);
      let userProfile = await getProfile(userId);
      
      // If no profile exists in the database, create it
      if (!userProfile) {
        console.log('[AuthContext] ====== PROFILE_NOT_FOUND - CREATING PROFILE ======');
        
        // Get the stored email for profile creation
        const email = localStorage.getItem('last_user_email') || 'unknown@example.com';
        console.log('[AuthContext] Using email for profile creation:', email);
        
        // First ensure the user exists in the public.users table
        console.log('[AuthContext] Creating user record before profile...');
        await createUser(userId, email);
        
        // Then create the profile
        console.log('[AuthContext] Creating profile record...');
        userProfile = await createProfile({
          id: userId,
          email: email,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          settings: null
        });
        
        if (userProfile) {
          console.log('[AuthContext] ✅ Profile created successfully:', userProfile);
          setUser(userProfile);
          return;
        } else {
          // If profile creation failed, try again with user check
          console.log('[AuthContext] ⚠️ First profile creation attempt failed, checking if user exists...');
          const userExists = await checkUserExists(userId);
          console.log(`[AuthContext] User exists check: ${userExists ? 'YES' : 'NO'} for ${userId}`);
          
          if (!userExists) {
            // Try to create the user one more time
            console.log('[AuthContext] Creating user record again...');
            await createUser(userId, email);
            
            // And try profile creation again
            console.log('[AuthContext] Attempting profile creation again...');
            userProfile = await createProfile({
              id: userId,
              email: email,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              settings: null
            });
            
            if (userProfile) {
              console.log('[AuthContext] ✅ Profile created successfully on second attempt:', userProfile);
              setUser(userProfile);
              return;
            } else {
              console.error('[AuthContext] ❌ Profile creation failed after second attempt');
            }
          }
          
          // If profile creation still failed, log error and fall back to generated profile
          console.error('[AuthContext] ====== EMERGENCY FALLBACK - PROFILE CREATION FAILED ======');
          
          // Get stored email or use a fallback
          const storedEmail = localStorage.getItem('last_user_email');
          if (storedEmail) {
            console.log(`[AuthContext] Using stored email for fallback profile: ${storedEmail}`);
          }
          
          // Create a fallback profile (does not persist to Supabase)
          const fallbackProfile: Profile = {
            id: userId,
            email: storedEmail || 'user@example.com',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            settings: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          console.log('[AuthContext] Set fallback profile:', fallbackProfile);
          setUser(fallbackProfile);
        }
      } else {
        // We got a profile, set it
        console.log(`[AuthContext] ✅ Found existing profile in database:`, userProfile);
        setUser(userProfile);
      }
      
    } catch (error) {
      console.error(`[AuthContext] ❌ ERROR fetching/creating profile for userId: ${userId}`, error);
      
      // Create a fallback profile but log the error
      const email = localStorage.getItem('last_user_email') || 'unknown@example.com';
      const fallbackProfile: Profile = {
        id: userId,
        email: email,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        settings: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log('[AuthContext] Using emergency fallback profile after error:', fallbackProfile);
      setUser(fallbackProfile);
    }
  }, [isAuthenticated, user]);
  
  // Reset Auth State (Restored)
  const resetAuthenticationState = useCallback(() => {
    logAuth('info', 'Starting complete authentication state reset');
    logAuth('info', 'Executing complete authentication state reset');
    logLocalStorageDetails('BEFORE_AUTH_RESET');
    document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'auth_status=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('auth_token'); // Flag
    localStorage.removeItem('user'); // Old user object storage
    localStorage.removeItem('auth_error');
    localStorage.removeItem('expired_token');
    localStorage.removeItem('auth_token_error');
    setUser(null); // Clear profile state
    setIsAuthenticated(false);
    logLocalStorageDetails('AFTER_AUTH_RESET');
    logAuth('info', 'Authentication state has been reset');
  }, []);

  // Check Session (Restored & Modified to fetch profile and handle rate limits)
  const checkSession = useCallback(async () => {
    try {
      // CRITICAL FIX: Add proper debouncing/throttling to prevent excessive calls
      const now = Date.now();
      
      // If another session check is already in progress, don't start a new one
      if (isCheckingSession) {
        logAuth('info', '[AuthContext] Session check already in progress, skipping');
        return false;
      }
      
      // CRITICAL: If we already have a valid session, don't check again repeatedly
      // This prevents the feedback loop that causes rate limiting
      if (isAuthenticated && user) {
        // If we've checked recently, don't check again
        const timeSinceLastCheck = now - lastSessionCheckTime;
        if (timeSinceLastCheck < SESSION_CHECK_MIN_INTERVAL) {
          logAuth('info', `[AuthContext] Session already authenticated with user, skipping check (last check ${Math.round(timeSinceLastCheck/1000)}s ago)`);
          return true;
        }
        
        // For authenticated users, we'll check much less frequently (every 5 min)
        if (timeSinceLastCheck < SESSION_CHECK_MIN_INTERVAL * 5) {
          logAuth('info', `[AuthContext] Reducing session check frequency for authenticated users`);
          return true;
        }
      }
      
      // Check if we've made a request too recently for non-authenticated states
      if (now - lastSessionCheckTime < SESSION_CHECK_MIN_INTERVAL) {
        logAuth('info', `[AuthContext] Session check throttled (checked ${(now - lastSessionCheckTime) / 1000}s ago, minimum interval is ${SESSION_CHECK_MIN_INTERVAL / 1000}s)`);
        sessionCheckCount++;
        
        // If we're already authenticated, just return that status
        if (isAuthenticated && user) {
          logAuth('info', '[AuthContext] Skipping throttled session check - already authenticated');
          return true;
        }
        
        // If getting repeated calls during initialization, try checking cookies directly
        if (document.cookie.includes('auth_status=logged_in')) {
          logAuth('info', '[AuthContext] Found auth_status cookie during throttled check');
          
          // Get the user ID from localStorage if available
          const cachedUserId = localStorage.getItem('last_user_id');
          if (cachedUserId && !user) {
            logAuth('info', '[AuthContext] Found cached user ID during throttled check, fetching profile');
            setIsAuthenticated(true);
            await fetchUserProfile(cachedUserId);
            return true;
          }
        }
        
        return false;
      }
      
      // Mark session check as in progress
      isCheckingSession = true;
      
      // Update timestamp regardless of success/failure
      lastSessionCheckTime = now;
      sessionCheckCount = 0;
      
      logAuth('info', 'BEGIN_SESSION_CHECK');
      console.log('[AuthContext] CHECK_SESSION_INVOKED. Current user:', user, 'isAuthenticated:', isAuthenticated);
      
      // Construct the session URL based on environment
      let sessionUrl = '';
      const currentAuthApiBaseUrl = getAuthApiBaseUrl();
      const currentEnvironment = detectEnvironment();
      
      if (isProduction()) {
        sessionUrl = `${currentAuthApiBaseUrl}/api/auth/session`;
      } else {
        sessionUrl = buildApiUrl('/api/auth/session', getAuthApiBaseUrl);
      }
      
      logAuth('info', 'DEBUG - URL construction for session check:', { 
        authApiBaseUrl: currentAuthApiBaseUrl,
        environment: currentEnvironment,
        finalUrl: sessionUrl 
      });
      
      logLocalStorageDetails('BEFORE_SESSION_CHECK');
      // --- DEBUGGING START ---
      logAuth('info', '[AuthContext checkSession] Cookies BEFORE fetch:', document.cookie);
      // --- DEBUGGING END ---
      
      // ENHANCED: Include explicit cache and retry control with keepalive
      const response = await fetch(sessionUrl, {
        method: 'GET',
        credentials: 'include',
        keepalive: true,
        headers: { 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      // ENHANCED: Additional debugging for session check response
      logAuth('info', 'SESSION_CHECK_RESPONSE', { 
        status: response.status, 
        ok: response.ok,
        statusText: response.statusText,
        redirected: response.redirected,
        url: response.url
      });
      
      // ENHANCED: Check for auth_token and auth_status cookies explicitly
      const hasAuthCookie = document.cookie.includes('auth_token=');
      const hasStatusCookie = document.cookie.includes('auth_status=logged_in');
      logAuth('info', '[AuthContext] Direct cookie check during session check:', {
        hasAuthCookie,
        hasStatusCookie,
        cookieString: document.cookie ? `Present (${document.cookie.split(';').length} cookies)` : 'None',
        cookies: document.cookie.split(';').map(c => c.trim()).filter(c => c.startsWith('auth_')).map(c => c.split('=')[0])
      });
      
      // If auth token cookie exists but response is 401, try one more time
      // This helps with race conditions where cookie was just set but not picked up
      if (!response.ok && response.status === 401 && (hasAuthCookie || hasStatusCookie)) {
        logAuth('warn', '[AuthContext] Found auth cookie but received 401, retrying after delay');
        
        // Wait a moment for cookie to be fully available to the browser
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Try again with a fresh request
        const retryResponse = await fetch(sessionUrl, {
          method: 'GET',
          credentials: 'include',
          keepalive: true,
          headers: { 
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store'
        });
        
        logAuth('info', 'SESSION_CHECK_RETRY_RESPONSE', { 
          status: retryResponse.status, 
          ok: retryResponse.ok,
          url: retryResponse.url
        });
        
        // Use the retry response instead if it succeeded
        if (retryResponse.ok) {
          logAuth('info', '[AuthContext] Retry succeeded, using retry response');
          const data = await retryResponse.json();
          
          if (data.user?.id) {
            logAuth('info', '[AuthContext] Custom session valid after retry, user ID:', data.user.id);
            localStorage.setItem('last_user_id', data.user.id);
            setIsAuthenticated(true);
            await fetchUserProfile(data.user.id);
            logLocalStorageDetails('AFTER_VALID_SESSION_RETRY');
            return true;
          }
        }
      }
      
      // Handle rate limit errors - don't log out the user if it's just a rate limit
      if (response.status === 429) {
        logAuth('warn', '[AuthContext] Session check rate limited (429). Assuming session is still valid.');
        logAuth('info', 'SESSION_CHECK_RATE_LIMITED', { action: 'maintain_session' });
        failedChecksCount++;
        
        // If we're already authenticated with a user, maintain that state instead of logging out
        if (isAuthenticated && user) {
          logAuth('info', '[AuthContext] Maintaining existing authenticated session despite rate limit');
          return true;
        }
        
        // If we don't have a session yet, try checking the cookie directly
        if (document.cookie.includes('auth_status=logged_in')) {
          logAuth('info', '[AuthContext] Found auth_status cookie, assuming session is valid despite rate limit');
          
          // Get the user ID from localStorage if available
          const cachedUserId = localStorage.getItem('last_user_id');
          if (cachedUserId) {
            logAuth('info', '[AuthContext] Found cached user ID, fetching profile');
            setIsAuthenticated(true);
            await fetchUserProfile(cachedUserId);
            return true;
          }
        }
        
        // If we're getting consistently rate limited, increase the backoff time progressively
        if (failedChecksCount > 3) {
          logAuth('warn', `[AuthContext] Multiple rate limit errors (${failedChecksCount}), increasing backoff time`);
          // This will make the next check wait even longer
          lastSessionCheckTime = Date.now() + SESSION_CHECK_MIN_INTERVAL;
        }
      } else {
        // Reset failed checks counter if we got a non-429 response
        failedChecksCount = 0;
      }
      
      if (response.ok) {
        const data = await response.json(); // Expects { user: { id: string, email: string } } from backend

        // --- DEBUGGING START ---
        logAuth('info', '[AuthContext verifyTokenInternal] Received OK response from verify-token.');
        logAuth('info', '[AuthContext verifyTokenInternal] Cookies BEFORE delay:', document.cookie);
        // Add a small delay to allow browser to potentially process Set-Cookie
        await new Promise(resolve => setTimeout(resolve, 100)); 
        logAuth('info', '[AuthContext verifyTokenInternal] Cookies AFTER delay:', document.cookie);
        // --- DEBUGGING END ---

        if (data.user?.id) {
           logAuth('info', '[AuthContext] Custom session valid, user ID:', data.user.id);
           // Store the user ID for future reference in case of rate limits
           localStorage.setItem('last_user_id', data.user.id);
           // Ensure email is also stored if available from session data, for profile creation
           if (data.user.email) {
             localStorage.setItem('last_user_email', data.user.email);
             logAuth('info', '[AuthContext] Email from session data stored in localStorage:', data.user.email);
           } else {
             logAuth('warn', '[AuthContext] Email not available in session data. Profile creation might use fallback.');
           }
           setIsAuthenticated(true);
           console.log('[AuthContext] PRE_FETCH_PROFILE_FROM_CHECK_SESSION for userId:', data.user.id);
           await fetchUserProfile(data.user.id); // Fetch full profile from Supabase
           logLocalStorageDetails('AFTER_VALID_SESSION');
           return true;
        } else {
           throw new Error('Session check returned ok but no user data');
        }
      } else if (response.status !== 429) { // Only reset auth if it's not a rate limit error
        logAuth('info', '[AuthContext] Custom session invalid or expired');
        resetAuthenticationState(); // Clear state if session invalid
        logLocalStorageDetails('AFTER_INVALID_SESSION');
        return false;
      }
      return false;
    } catch (error) {
      logAuth('error', '[AuthContext] Error checking custom session:', error);
      logAuth('info', 'SESSION_CHECK_ERROR: ' + (error instanceof Error ? error.message : 'Unknown error'));
      
      // Only reset if we're not currently authenticated
      if (!isAuthenticated) {
        resetAuthenticationState(); // Clear state on error
      } else {
        logAuth('info', '[AuthContext] Maintaining session despite error checking it (user already authenticated)');
      }
      return false;
    } finally {
      // CRITICAL FIX: Always mark session check as complete to prevent locking
      isCheckingSession = false;
    }
  }, [resetAuthenticationState, fetchUserProfile, isAuthenticated, user]);

  // Initialization Effect (Restored & Modified)
  useEffect(() => {
    const initAuth = async () => {
      console.log('[AuthContext] INIT_AUTH_STARTED. hasInitRunRef.current:', hasInitRunRef.current, 'isInitializing:', isInitializing);
      // If already initialized or initializing is complete, don't re-run
      if (hasInitRunRef.current || !isInitializing) {
        return;
      }
      
      // Mark as initialized to prevent duplicate runs
      hasInitRunRef.current = true;
      
      logAuth('info', 'AUTH_INIT_START', { message: 'Starting custom authentication initialization' });
      logLocalStorageDetails('AUTH_INIT');
      
      // CRITICAL FIX: First check if we already have valid auth cookies before doing anything else
      if (document.cookie.includes('auth_status=logged_in') && document.cookie.includes('auth_token')) {
        logAuth('info', '[AuthContext] Found valid auth cookies during initialization, attempting to restore session');
        const cachedUserId = localStorage.getItem('last_user_id');
        
        if (cachedUserId) {
          try {
            logAuth('info', '[AuthContext] Using cached user ID for session restoration:', cachedUserId);
            setIsAuthenticated(true);
            console.log('[AuthContext] PRE_FETCH_PROFILE_FROM_INIT_AUTH (cached user) for userId:', cachedUserId);
            await fetchUserProfile(cachedUserId);
            setIsInitializing(false);
            logAuth('info', 'AUTH_INIT_COMPLETE_FROM_COOKIES', { isAuthenticated: true, hasUser: true, cachedUserId });
            return;
          } catch (error) {
            logAuth('error', '[AuthContext] Failed to restore session from cached data, continuing with normal flow', error);
            // Continue with normal initialization
          }
        }
      }
      
      // Check for token parameter in URL (handle magic links)
      try {
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          const token = url.searchParams.get('token');
          
          if (token) {
            logAuth('info', '[AuthContext] Found token in URL during initialization - attempting to verify');
            logTokenInfo(token, 'TOKEN_FOUND_IN_URL');
            
            try {
              // CRITICAL FIX: Mark verification in progress to prevent concurrent initializations
              localStorage.setItem('auth_verification_in_progress', 'true');
              
              try {
                // IMPORTANT: Call the public verifyToken function directly
                await verifyToken(token);
                
                // CRITICAL FIX: Remove the in-progress marker
                localStorage.removeItem('auth_verification_in_progress');
                
                // End initialization - we're logged in now
                setIsInitializing(false);
                logAuth('info', 'AUTH_INIT_COMPLETE_TOKEN_VERIFIED', { isAuthenticated: true });
                return;
              } catch (verifyError) {
                logAuth('error', '[AuthContext] Error verifying token from URL', verifyError);
                
                // CRITICAL FIX: Remove the in-progress marker even on error
                localStorage.removeItem('auth_verification_in_progress');
                
                // Continue with session check below
              }
            } catch (innerError) {
              logAuth('error', '[AuthContext] Inner error during verification', innerError);
              // Continue with session check below
            }
          }
        }
      } catch (urlError) {
        logAuth('error', '[AuthContext] Error checking URL for token', urlError);
      }
      
      // CRITICAL FIX: Check if token verification was completed by another component 
      // to avoid duplicate session checks
      if (document.cookie.includes('auth_status=logged_in') && localStorage.getItem('last_verified_token')) {
        logAuth('info', '[AuthContext] Token was already verified by another component, session is valid');
        const cachedUserId = localStorage.getItem('last_user_id');
        
        if (cachedUserId) {
          setIsAuthenticated(true);
          await fetchUserProfile(cachedUserId);
          setIsInitializing(false);
          logAuth('info', 'AUTH_INIT_COMPLETE_FROM_PREVIOUS_VERIFICATION', { isAuthenticated: true });
          return;
        }
      }
      
      // Use checkSession to verify cookie validity and fetch profile (only once during init)
      try {
        console.log('[AuthContext] PRE_CHECK_SESSION_FROM_INIT_AUTH');
        const sessionValid = await checkSession();
        console.log('[AuthContext] POST_CHECK_SESSION_FROM_INIT_AUTH. Session valid:', sessionValid);
        
        // Always update initialization state to false, even if session check failed
        logAuth('info', 'AUTH_INIT_COMPLETE', { 
          isAuthenticated, 
          hasUser: !!user,
          sessionValid
        });
      } catch (error) {
        logAuth('error', '[AuthContext] Error during session check in initialization', error);
      } finally {
        // CRITICAL FIX: Always complete initialization even if errors occur
        setIsInitializing(false);
      }
    };
    
    // Run initialization once
    initAuth();
    
    // Clean up URL parameters if needed
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('token')) {
          url.searchParams.delete('token');
          window.history.replaceState({}, document.title, url.pathname + url.search);
          logAuth('info', '[AUTH] Cleaned up URL parameters in initialization effect');
        }
      } catch (err) { 
        logAuth('error', '[AUTH] Error cleaning up URL parameters in initialization effect:', err); 
      }
    }
    
  }, []); // Empty dependency array since we're using hasInitRunRef.current to control execution

  // Login (Restored)
  const login = async (email: string) => {
     try {
       logAuth('info', 'LOGIN_START', { email });
       const authApiBaseUrl = getAuthApiBaseUrl();
       logAuth('info', 'DEBUG - Auth API Base URL:', authApiBaseUrl);
       
       // DIRECT FIX: Explicitly determine the correct URL based on environment
       const environment = detectEnvironment();
       let requestUrl;
       
       if (environment === 'staging' || environment === 'production') {
         // On staging/production, use the direct format with /api prefix
         requestUrl = `${authApiBaseUrl}/api/auth/magic-link`;
       } else {
         // In development, use buildApiUrl as before
         requestUrl = buildApiUrl('/api/auth/magic-link', getAuthApiBaseUrl);
       }
       
       logAuth('info', 'DEBUG - URL construction for login:', { 
         authApiBaseUrl,
         environment,
         finalUrl: requestUrl 
       });
       
       logAuth('info', 'Corrected Login Request URL:', requestUrl);
       logAuth('info', 'LOGIN_REQUEST', { url: requestUrl, email });
       const response = await fetch(requestUrl, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ email }),
       });
       logAuth('info', 'LOGIN_RESPONSE', { status: response.status, ok: response.ok });
       if (!response.ok) {
         let errorMessage = 'Failed to send magic link';
         try {
           const errorData = await response.json();
           errorMessage = errorData?.error || errorMessage;
           logAuth('info', 'LOGIN_ERROR_RESPONSE: ' + JSON.stringify({ errorData }));
         } catch (e) { logAuth('info', 'LOGIN_ERROR_PARSE_FAILED'); }
         throw new Error(errorMessage);
       }
       const data = await response.json();
       logAuth('info', 'LOGIN_SUCCESS', { hasMagicLink: !!data.devMagicLink });
       if (data.devMagicLink) {
          const url = new URL(data.devMagicLink);
          const token = url.searchParams.get('token');
          if(token) logTokenInfo(token, 'DEV_MAGIC_LINK_GENERATED');
       }
       return { devMagicLink: data.devMagicLink };
     } catch (error) {
       logAuth('error', 'Login error:', error);
       logAuth('info', 'LOGIN_EXCEPTION: ' + (error instanceof Error ? error.message : 'Unknown error'));
       throw error;
     }
   };

  // --- Verify Token (Restored & Modified) ---
   const cleanUpUrlParameters = () => {
      if (typeof window !== 'undefined') {
         try {
           const url = new URL(window.location.href);
           if (url.searchParams.has('token')) {
             logAuth('info', 'URL_CLEANUP', { hadToken: true });
             url.searchParams.delete('token');
             window.history.replaceState({}, document.title, url.pathname + url.search);
             logAuth('info', '[AUTH] Cleaned up URL parameters, removed token');
           }
         } catch (err) { logAuth('error', '[AUTH] Error cleaning up URL parameters:', err); }
      }
    };
    
  const verifyTokenInternal = async (token: string) => {
    try {
      console.log('[AuthContext] Starting internal token verification');
      logTokenInfo(token, 'TOKEN_VERIFY_STARTING');
      
      // Determine the API URL based on the environment
      let verifyUrl;
      const currentAuthApiBaseUrl = getAuthApiBaseUrl();
      
      if (isProduction()) {
        // For deployment in production/staging environments
        verifyUrl = `${currentAuthApiBaseUrl}/api/auth/verify-token`;
      } else {
        // For local development or when API is on a different domain
        verifyUrl = buildApiUrl('/api/auth/verify-token', getAuthApiBaseUrl);
      }
      
      console.log('[AuthContext] Using verify URL:', verifyUrl);
      
      // Send the token to the verify endpoint
      const response = await fetch(verifyUrl, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store'
        },
        body: JSON.stringify({ token })
      });

      // Log response details for debugging
      console.log('[AuthContext] Verify token response:', {
        status: response.status, 
        ok: response.ok,
        url: response.url
      });
      
      // Handle error responses
      if (!response.ok) {
        let errorMessage = 'Invalid or expired token';
        let errorResponseData = null;
        try {
           errorResponseData = await response.json();
           errorMessage = errorResponseData?.error || errorMessage;
           logAuth('info', 'VERIFY_TOKEN_ERROR_RESPONSE: ' + JSON.stringify({ status: response.status, errorData: errorResponseData }));
        } catch (e) { 
           logAuth('info', 'VERIFY_TOKEN_ERROR_PARSE_FAILED: ' + JSON.stringify({ status: response.status })); 
           // If parsing fails, the original message stands
        }
        // Store error for LoginPage to potentially pick up
        localStorage.setItem('auth_token_error', errorMessage);
        localStorage.setItem('expired_token', token);
        
        // Let the VerifyPage component handle redirections
        throw new Error(errorMessage); 
      }
      
      const data = await response.json();
      
      if (data.user?.id && data.user?.email) { // Ensure email is also present
          console.log('[AuthContext] ====== VERIFY_TOKEN_SUCCESS ======');
          console.log('[AuthContext] Verified user ID:', data.user.id, 'Email:', data.user.email);
          
          // Store user ID and email for persistence and profile creation
          localStorage.setItem('last_user_id', data.user.id);
          localStorage.setItem('last_user_email', data.user.email);
          
          // CRITICAL CHANGE: Force profile creation FIRST before anything else
          console.log('[AuthContext] ====== STARTING IMMEDIATE PROFILE CREATION ======');
          try {
            console.log('[AuthContext] Directly calling fetchUserProfile for ID:', data.user.id);
            await fetchUserProfile(data.user.id);
            console.log('[AuthContext] ====== PROFILE CREATION COMPLETED SUCCESSFULLY ======');
          } catch (profileError) {
            console.error('[AuthContext] ====== PROFILE CREATION FAILED ======', profileError);
            // Continue with authentication even if profile creation fails
            // This way the user can at least log in, and we can try again later
          }
          
          // DIAGNOSTIC: Add deliberate delay to wait for cookies to be set
          console.log('[AuthContext] Waiting 500ms for cookies to be processed...');
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Update authentication state in the context
          setUser(prevUser => {
            const userObj = {
              id: data.user.id,
              email: data.user.email, 
              timezone: prevUser?.timezone || 'America/New_York',
              created_at: prevUser?.created_at || new Date().toISOString(),
              updated_at: prevUser?.updated_at || new Date().toISOString(),
              settings: prevUser?.settings || null
            };
            
            localStorage.setItem('user', JSON.stringify(userObj));
            localStorage.setItem('auth_timestamp', Date.now().toString());
            
            return userObj;
          });
          
          setIsAuthenticated(true);
          console.log('[AuthContext] User authenticated with ID:', data.user.id);
          
          // DIAGNOSTIC: Final cookie check before returning
          console.log('[AuthContext] Final cookie check:', {
            cookiesPresent: !!document.cookie,
            authTokenPresent: document.cookie.includes('auth_token'),
            authStatusPresent: document.cookie.includes('auth_status'),
            cookieCount: document.cookie ? document.cookie.split(';').length : 0
          });
      } else {
          throw new Error('Verification succeeded but no user data returned');
      }
    } catch (error) {
      // Add more detailed logging here
      console.error('[AuthContext] Verification Error:', error);
      logAuth('info', 'VERIFY_TOKEN_EXCEPTION: ' + (error instanceof Error ? error.message : 'Unknown error'));
      
      // DIAGNOSTIC: Final error state cookie check
      logAuth('info', '[DIAGNOSTIC] Cookies after error:', document.cookie ? 
        `Present (${document.cookie.split(';').length} cookies)` : 'None');
      
      resetAuthenticationState(); 
      logLocalStorageDetails('AFTER_FAILED_VERIFICATION');
      throw error; // Re-throw error to be caught by VerifyPage
    } finally {
       setIsInitializing(false);
    }
  };

  // Public verifyToken just calls internal one (can add browser checks back if needed)
  const verifyToken = async (token: string) => {
    try {
      // Check for existing auth before attempting verification
      if (isAuthenticated && user) {
        logAuth('info', '[AuthContext verifyToken] Already authenticated, skipping token verification');
        return;
      }
      
      // Prevent multiple verification attempts for the same token
      const previousToken = localStorage.getItem('last_verified_token');
      if (previousToken === token) {
        logAuth('info', '[AuthContext verifyToken] Token already verified recently, skipping duplicate verification');
        
        // If user is not set yet but we have authentication, try to restore from cached data
        if (isAuthenticated && !user) {
          const cachedUserId = localStorage.getItem('last_user_id');
          if (cachedUserId) {
            await fetchUserProfile(cachedUserId);
          }
        }
        
        return;
      }
      
      // Proceed with verification
      await verifyTokenInternal(token);
      
      // Store verified token to prevent duplicate verifications
      localStorage.setItem('last_verified_token', token);
      
      // Ensure initialization state is completed after verification
      setIsInitializing(false);
    } catch (error) {
      logAuth('error', '[AuthContext verifyToken] Verification error:', error);
      // Clear initialization state even on error
      setIsInitializing(false);
      throw error;
    }
  };

  // Logout (Restored)
  const logout = useCallback(async (): Promise<void> => {
    try {
      logAuthEvent('LOGOUT_START', { timestamp: new Date().toISOString() });
      
      // Determine the correct API endpoint URL
      let logoutUrl = `${getAuthApiBaseUrl()}/api/auth/logout`;
      
      logAuthEvent('DEBUG - URL construction for logout', {
        apiBaseUrl: getAuthApiBaseUrl(),
        logoutUrl
      });
      
      console.log('[AuthContext] Corrected Logout Request URL:', logoutUrl);
      logAuthEvent('LOGOUT_REQUEST', { url: logoutUrl });
      
      // Call the server to clear the auth cookie
      await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Client-side cleanup
      console.log('[AuthContext] Starting complete authentication state reset');
      
      // Manual cookie cleanup in the browser - much more aggressive approach
      const cookieDomain = window.location.hostname.includes('sequenceplanner.com') 
        ? `.${window.location.hostname.split('.').slice(-2).join('.')}` 
        : undefined;
       
       // Get the base hostname without subdomains for broad clearing
       const hostname = window.location.hostname;
       const baseDomain = hostname.split('.').slice(-2).join('.');
       
       // Log the domains we're clearing
       console.log('[AuthContext] Clearing cookies with domains:', {
         cookieDomain,
         hostname,
         baseDomain
       });
       
       // The most comprehensive set of cookie clearing strategies
       const cookiesToClear = ['auth_token', 'auth_status'];
       
       // Helper function to clear a cookie with all possible variations
       const clearCookieAggressively = (name: string) => {
         // Basic - no domain
         document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
         
         // With SameSite variations
         document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; samesite=lax`;
         document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; samesite=none`;
         
         // Try with hostname
         document.cookie = `${name}=; path=/; domain=${hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
         
         // Try with base domain
         if (hostname !== baseDomain) {
           document.cookie = `${name}=; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
           document.cookie = `${name}=; path=/; domain=.${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
         }
         
         // Try with dot format
         if (cookieDomain) {
           document.cookie = `${name}=; path=/; domain=${cookieDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
         }
         
         // With secure flag for HTTPS
         if (window.location.protocol === 'https:') {
           document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure`;
           document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure; samesite=lax`;
           document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure; samesite=none`;
           
           if (hostname !== baseDomain) {
             document.cookie = `${name}=; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure`;
             document.cookie = `${name}=; path=/; domain=.${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure`;
           }
           
           if (cookieDomain) {
             document.cookie = `${name}=; path=/; domain=${cookieDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; secure`;
           }
         }
       };
       
       // Clear each cookie aggressively
       cookiesToClear.forEach(clearCookieAggressively);
       
       // Extra check - if cookies still exist, log it
       const cookiesAfterClearing = document.cookie;
       console.log('[AuthContext] Cookies after clearing attempts:', cookiesAfterClearing);
       
       // Clear all authentication state from memory and localStorage
       console.log('[AuthContext] Executing complete authentication state reset');
       logLocalStorageDetails('BEFORE_RESET');
       
       // Reset auth-related localStorage
       localStorage.removeItem('user');
       localStorage.removeItem('auth_token');
       localStorage.removeItem('auth_timestamp');
       localStorage.removeItem('last_user_id');
       localStorage.removeItem('last_user_email');
       
       logLocalStorageDetails('AFTER_RESET');
       
       // Reset state
       setUser(null);
       setIsAuthenticated(false);
       setIsInitializing(false);
       
       console.log('[AuthContext] Authentication state has been reset');
       logAuthEvent('LOGOUT_COMPLETE: Logout completed', {});
       
       // Navigate to login page after cleaning up
       // Use window.location for a hard redirect to ensure clean state
       window.location.href = '/login';
     } catch (error) {
       console.error('[AuthContext] Error during logout:', error);
       logAuthEvent('LOGOUT_ERROR', { error: (error as Error).message });
       
       // Still clean up local state even if server logout fails
       setUser(null);
       setIsAuthenticated(false);
       setIsInitializing(false);
       
       localStorage.removeItem('user');
       localStorage.removeItem('auth_token');
       localStorage.removeItem('auth_timestamp');
       localStorage.removeItem('last_user_id');
       localStorage.removeItem('last_user_email');
       
       // Still redirect to login page on error
       window.location.href = '/login';
     }
   }, [logLocalStorageDetails, logAuthEvent]);
 
   // Update User Preferences (Keep as is, uses Supabase)
   const updateUserPreferences = async (updates: { 
     timezone?: string; 
     settings?: Database['public']['Tables']['profiles']['Update']['settings']; 
   }) => {
     if (!user?.id) {
       logAuth('error', '[AuthContext] Cannot update preferences - user not authenticated');
       return { error: new Error('User not authenticated') };
     }
     logAuth('info', `[AuthContext] Updating user preferences: ${user.id}`, updates);
     
     try {
       // Handle mock/development environment differently
       const environment = detectEnvironment();
       if (environment === 'development' || environment === 'staging') {
         logAuth('info', `[AuthContext] ${environment.toUpperCase()} MODE: Using local profile update`);
         // Update the user state directly with the new preferences
         const updatedProfile: Profile = {
           ...user,
           ...updates,
           updated_at: new Date().toISOString()
         };
         setUser(updatedProfile);
         logAuth('info', '[AuthContext] Updated local profile:', updatedProfile);
         return {};
       }
       
       // Only use Supabase in production
       try {
         // For production, use Supabase
         const updatedProfile = await updateProfile({ id: user.id, ...updates });
         if (updatedProfile) {
           logAuth('info', '[AuthContext] Profile updated successfully in Supabase:', updatedProfile);
           setUser(updatedProfile); // Update local state
           return {};
         } else {
           logAuth('error', '[AuthContext] Profile update returned null');
           // Fall back to local update instead of throwing
           const localUpdatedProfile: Profile = {
             ...user,
             ...updates,
             updated_at: new Date().toISOString()
           };
           setUser(localUpdatedProfile);
           logAuth('info', '[AuthContext] Fell back to local profile update:', localUpdatedProfile);
           return {};
         }
       } catch (supabaseError) {
         logAuth('error', '[AuthContext] Supabase error updating preferences:', supabaseError);
         // Fall back to local update on Supabase error
         const localUpdatedProfile: Profile = {
           ...user,
           ...updates,
           updated_at: new Date().toISOString()
         };
         setUser(localUpdatedProfile);
         logAuth('info', '[AuthContext] Fell back to local profile update after Supabase error:', localUpdatedProfile);
         return {};
       }
     } catch (error) {
       logAuth('error', '[AuthContext] Error updating preferences:', error);
       return { error: error instanceof Error ? error : new Error('Failed to update preferences') };
     }
   };
 
   // Log final state before providing value
   logAuth('info', 'Provider Render State:', { isAuthenticated, userId: user?.id, isInitializing });
 
   const value = {
     isAuthenticated,
     user,
     login,
     verifyToken,
     logout,
     isInitializing,
     resetAuthenticationState,
     updateUserPreferences
   };
 
   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};